import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { authStore } from './store/authStore';
import { consumeSessionExpiredNotice } from './api/client';
import { PrivateRoute } from './components/shared/PrivateRoute';
import { useSyncEngine } from './offline/useSyncEngine';
import { SyncStatusPanel } from './offline/SyncStatusPanel';
import { isDesktopRuntime } from './offline/db';
import './App.css';

// Lazy-loaded pages — each becomes a separate webpack chunk
const LoginPage = lazy(() => import('./pages/Auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/Auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/Auth/VerifyEmailPage'));
const ConfirmEmailChangePage = lazy(() => import('./pages/Auth/ConfirmEmailChangePage'));
const ResendVerificationPage = lazy(() => import('./pages/Auth/ResendVerificationPage'));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/Auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'));
const MyAccountPage = lazy(() => import('./pages/Account/MyAccountPage'));
const PatientDashboardPage = lazy(() => import('./pages/Dashboard/PatientDashboardPage'));
const SearchProfessionalsPage = lazy(() => import('./pages/Professional/SearchProfessionalsPage'));
const ProfessionalDetailPage = lazy(() => import('./pages/Professional/ProfessionalDetailPage'));
const ProfessionalDashboardPage = lazy(() => import('./pages/Professional/ProfessionalDashboardPage'));
const MedicalStaffDashboardPage = lazy(() => import('./pages/Dashboard/MedicalStaffDashboardPage'));
const InformationPage = lazy(() => import('./pages/Info/InformationPage'));
const TermsPage = lazy(() => import('./pages/Legal/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/Legal/PrivacyPolicyPage'));
const PlanesPage = lazy(() => import('./pages/Info/PlanesPage'));
const ClinicDetailPage = lazy(() => import('./pages/Clinic/ClinicDetailPage'));
const ClinicDashboardPage = lazy(() => import('./pages/Clinic/ClinicDashboardPage'));
const ClinicBookingPage = lazy(() => import('./pages/Clinic/ClinicBookingPage'));
const ConsultationPage = lazy(() => import('./pages/Consultation').then(m => ({ default: m.ConsultationPage })));
const ConsultTemplateSettings = lazy(() => import('./pages/Consultation').then(m => ({ default: m.ConsultTemplateSettings })));
const PlanDetailPage = lazy(() => import('./pages/Plans/PlanDetailPage'));
const PayPalReturnPage = lazy(() => import('./pages/Billing/PayPalReturnPage'));
const PayPalCancelPage = lazy(() => import('./pages/Billing/PayPalCancelPage'));
const RenewGuestSubscriptionPage = lazy(() => import('./pages/Billing/RenewGuestSubscriptionPage'));
const SharedBundlePage = lazy(() => import('./pages/Healthcare/SharedBundlePage'));

// Loaded via file:// in the desktop shell, BrowserRouter's first
// history.replaceState() call (the "/" -> "/login" redirect below) throws
// an uncaught SecurityError -- file:// documents have an opaque origin, and
// Chromium rejects a history entry whose URL path differs from the
// currently loaded file. That exception happens during the very first
// render, so the whole app renders blank with no on-screen error. HashRouter
// never touches the History API's URL argument, so it's unaffected; the web
// build keeps BrowserRouter's clean URLs.
const Router = isDesktopRuntime() ? HashRouter : BrowserRouter;

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Main App Component
 */
function App() {
  // Load auth state from localStorage on app startup
  useEffect(() => {
    authStore.getState().loadFromStorage();
  }, []);

  // No-ops outside the Electron desktop shell (isDesktopRuntime() guards).
  useSyncEngine();

  // One-shot notice for the auto-reload in api/client.ts's response
  // interceptor: when a session's refresh token turns out to be dead (expired
  // past its own lifetime, or blacklisted -- e.g. evicted by the
  // 2-concurrent-session cap after logging in elsewhere), the app reloads
  // itself clean rather than leaving public pages stuck on failed queries
  // with no token. Surface that here so it reads as an explained recovery,
  // not the page silently changing out from under the user.
  const [showSessionExpiredNotice, setShowSessionExpiredNotice] = useState(false);
  useEffect(() => {
    if (consumeSessionExpiredNotice()) {
      setShowSessionExpiredNotice(true);
    }
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          {showSessionExpiredNotice && (
            <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm text-center py-2 px-4">
              Tu sesión anterior expiró (posiblemente por haber iniciado sesión en otro dispositivo). Te reconectamos automáticamente.
              <button
                onClick={() => setShowSessionExpiredNotice(false)}
                className="ml-3 font-medium underline hover:no-underline"
              >
                Cerrar
              </button>
            </div>
          )}
          <SyncStatusPanel />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-cream"><div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/confirm-email-change/:token" element={<ConfirmEmailChangePage />} />
          <Route path="/resend-verification" element={<ResendVerificationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* New Public Landing Page: Professional Search -- the desktop
              shell is a staff-only tool (doctors/assistants), it has no
              use for the patient-facing "search for a doctor" homepage,
              so it goes straight to login instead (which itself already
              redirects an already-authenticated user on to their
              dashboard). */}
          <Route path="/" element={isDesktopRuntime() ? <Navigate to="/login" replace /> : <SearchProfessionalsPage />} />
          
          {/* Information Page */}
          <Route path="/information" element={<InformationPage />} />

          {/* Legal documents -- public, no auth required (Store submission
              and every account role, including guests, needs a reachable
              URL for each) */}
          <Route path="/terminos" element={<TermsPage />} />
          <Route path="/privacidad" element={<PrivacyPolicyPage />} />
          
          {/* Planes Page */}
          <Route path="/planes" element={<PlanesPage />} />

          {/* Professional Detail Page with SEO-friendly slug */}
          <Route path="/professional/:slug" element={<ProfessionalDetailPage />} />

          {/* Clinic Public Page */}
          <Route path="/clinic/:slug" element={<ClinicDetailPage />} />
          <Route path="/clinic/:slug/book" element={<ClinicBookingPage />} />

          {/* Protected Routes */}
          <Route
            path="/professional/dashboard"
            element={
              <PrivateRoute>
                <ProfessionalDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/clinic/:slug/dashboard"
            element={
              <PrivateRoute>
                <ClinicDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/patient/dashboard"
            element={
              <PrivateRoute>
                <PatientDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/medical-staff/dashboard"
            element={
              <PrivateRoute>
                <MedicalStaffDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/account"
            element={
              <PrivateRoute>
                <MyAccountPage />
              </PrivateRoute>
            }
          />

          {/* Consultation Routes */}
          <Route
            path="/consultation"
            element={
              <PrivateRoute>
                <ConsultationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/consult-templates"
            element={
              <PrivateRoute>
                <ConsultTemplateSettings />
              </PrivateRoute>
            }
          />

          <Route path="/plans/:plan" element={<PlanDetailPage />} />
          <Route path="/billing/paypal/return" element={<PayPalReturnPage />} />
          <Route path="/billing/paypal/cancel" element={<PayPalCancelPage />} />
          <Route path="/renew/:publicId" element={<RenewGuestSubscriptionPage />} />

          {/* Share Bundle (Remisión) */}
          <Route
            path="/remision/:token"
            element={
              <PrivateRoute>
                <SharedBundlePage />
              </PrivateRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </Suspense>
      </Router>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
